<template>
    <DCard padding>
        <DRow
            align-horizontal="start"
            align-vertical="end"
            class="mb-3"
        >
            <DCol lg="2">
                <DDateFormatInput
                    id="delivery_notes_date"
                    v-model="filterData.date"
                    format="YYYY-MM-DD"
                    :have-class="'mb-0'"
                    :label="__('customers.delivery_notes.table.date')"
                    class="mb-0"
                    type="date"
                />
            </DCol>
            <DCol lg="2">
                <DInput
                    id="delivery_notes_delivery_note_number"
                    v-model="filterData.delivery_note"
                    :label="__('customers.delivery_notes.table.delivery_note_number')"
                    type="number"
                />
            </DCol>
            <DCol lg="2">
                <DInput
                    id="delivery_notes_order_number"
                    v-model="filterData.order_number"
                    :label="__('customers.delivery_notes.table.order_number')"
                />
            </DCol>
            <DCol
                lg="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="changePage"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DRow class="mt-3">
            <DCol>
                <DTable
                    :items="deliveryNotes.data"
                    :fields="fields"
                >
                    <template #customer_cost_center="{item}">
                        <td>{{ item.customer_cost_center ?? '' }}</td>
                    </template>
                    <template #order_number="{item}">
                        <td>{{ item.order_number ?? '' }}</td>
                    </template>
                    <template #actions="{item}">
                        <td>
                            <DDropdown
                                :id="`delivery_notes_dropdown_${item.id}`"
                                :class="'more-actions-button'"
                                :toggler-text="__('customers.delivery_notes.buttons.actions')"
                            >
                                <DDropdownItem
                                    :id="`delivery_notes_cancel_${item.id}`"
                                    icon="close"
                                    @click="openDeleteModal(item.id)"
                                >
                                    {{ __('customers.delivery_notes.buttons.cancel') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    :id="`delivery_notes_download_${item.id}`"
                                    icon="download"
                                    :href="downloadDeliveryNote(item.id)"
                                >
                                    {{ __('customers.delivery_notes.buttons.download') }}
                                </DDropdownItem>
                            </DDropdown>
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="deliveryNotes.last_page > 1"
                    :active-page="deliveryNotes.current_page"
                    :pages="deliveryNotes.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
    </DCard>

    <DFabs>
        <SSOFab
            :customer="customer"
            @open-modal="showCreateCloudpitCustomerModal = true"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
        <DFab
            v-if="userPermissions.includes('customers.delivery_note')"
            id="btn-add"
            icon="plus"
            :label="__('customers.delivery_notes.buttons.create')"
            @click="createDeliveryNote"
        />
    </DFabs>
    <DModal
        :show="showDeleteModal"
        :title="__('customers.delivery_notes.messages.delete_title')"
        :message="__('customers.delivery_notes.messages.delete_body')"
        is-confirm
        is-error
        @confirm="cancelDeliveryNote"
        @close="closeDeleteModal"
    />
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import { ref, computed } from 'vue'
import SSOFab from '~@/Components/Customers/SSOFab'

export default {
    layout: [App, CustomerBase]
}
</script>

<script setup>

const fields = [
    { key: 'id', label: __('customers.delivery_notes.table.id') },
    { key: 'created_at', label: __('customers.delivery_notes.table.date') },
    { key: 'delivery_note_number', label: __('customers.delivery_notes.table.delivery_note_number') },
    { key: 'order_number', label: __('customers.delivery_notes.table.order_number') },
    { key: 'customer_cost_center', label: __('customers.delivery_notes.table.customer_cost_center') },
    { key: 'actions', label: '', _style: { minWidth: '5%' } }
]

const props = defineProps({
    deliveryNotes: {
        type: Object,
        default: () => {
            return {}
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    },
    customer: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const filterData = ref({
    date: props.filters.date ?? null,
    delivery_note: props.filters.delivery_note ?? null,
    order_number: props.filters.order_number ?? null
})

const showDeleteModal = ref(false)
const selectedDeliveryNote = ref(null)

function openDeleteModal (item) {
    showDeleteModal.value = true
    selectedDeliveryNote.value = item
}

function closeDeleteModal () {
    showDeleteModal.value = false
    selectedDeliveryNote.value = null
}

const userPermissions = computed(() => usePage().props.auth.permissions)

const cancelDeliveryNote = () => router.delete(route('customers.customers.delivery_notes.destroy', { customer: props.customer.id, delivery_note: selectedDeliveryNote.value }))

const downloadDeliveryNote = (item) => route('customers.customers.delivery_notes.download', { customer: props.customer.id, delivery_note: item })

const createDeliveryNote = () => router.get(route('customers.customers.delivery_notes.create', { customer: props.customer.id }))

const changePage = (page) => {
    const query = Object.fromEntries(Object.entries(filterData.value).filter(([_, v]) => v))
    if (page > 1) {
        query.page = page
    }

    router.get(route('customers.customers.delivery_notes.index', { customer: props.customer.id }), query)
}

const resetFilters = () => {
    filterData.value.date = null
    filterData.value.delivery_note = null
    filterData.value.order_number = null
    changePage()
}

</script>
